import type { SectionChange } from "../../shared/goals/library";
import { post, request } from "./api";

export type LibraryOrigin = "flashadz" | "account" | "product";

/** One item in the library list: its latest version, every version, and a newer Flashadz version when there is one. */
export interface LibraryEntry {
  kind: string;
  name: string;
  origin: LibraryOrigin;
  productId: string | null;
  latest: { id: string; version: number; title: string; description: string; inShort: string; tagline: string; draft: boolean; createdAt: string };
  versions: { id: string; version: number; draft: boolean; createdAt: string; note: string }[];
  newerDefault: { id: string; version: number } | null;
}

/** One version of an item. The body is markdown with `## ` sections. */
export interface LibraryItem {
  id: string;
  kind: string;
  name: string;
  version: number;
  title: string;
  body: string;
  meta: Record<string, unknown>;
  origin: LibraryOrigin;
  productId: string | null;
  parentId: string | null;
  createdAt: string;
}

export const libraryApi = {
  list: (productId?: string) =>
    request<{ items: LibraryEntry[] }>(`/library${productId ? `?productId=${encodeURIComponent(productId)}` : ""}`),
  item: (id: string) => request<{ item: LibraryItem }>(`/library/items/${encodeURIComponent(id)}`),
  /** What changed from `otherId` (the older version) to `id`, section by section. */
  compare: (id: string, otherId: string) =>
    request<{ changes: SectionChange[] }>(`/library/items/${encodeURIComponent(id)}/compare/${encodeURIComponent(otherId)}`),
  /** Save the owner's edit as the account's own next version. Editing a Flashadz item makes the account's copy. */
  saveVersion: (id: string, body: { body: string; title?: string; note?: string }) =>
    post<{ item: LibraryItem }>(`/library/items/${encodeURIComponent(id)}/versions`, body),
  /** Take Flashadz's newest version into the account's own copy (`id`), as the copy's next version. */
  takeDefault: (id: string) => post<{ item: LibraryItem }>(`/library/items/${encodeURIComponent(id)}/take-default`),
  keep: (id: string) => post<{ ok: boolean }>(`/library/items/${encodeURIComponent(id)}/keep`),
  /** Keep the account's own version (`id`, any of its versions) over Flashadz's newer one. */
  keepMine: (id: string) => post<{ ok: boolean }>(`/library/items/${encodeURIComponent(id)}/keep-mine`),
};
